import { useEffect } from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { type AssetsSlice, createAssetsSlice } from './assetsSlice'
import { type AutoSaveSlice, createAutoSaveSlice } from './autoSaveSlice'
import {
  type ConfigurationSlice,
  createConfigurationSlice,
} from './configurationSlice'
import {
  createDesignHistorySlice,
  type DesignHistorySlice,
  EMPTY_DESIGN_HISTORY,
} from './designHistorySlice'
import { createPreviewSlice, type PreviewSlice } from './previewSlice'
import {
  createRulesMergeHistorySlice,
  EMPTY_RULES_MERGE_HISTORY,
  type RulesMergeHistorySlice,
} from './rulesMergeHistorySlice'

// Combined store interface
export interface ChatBuilderStore
  extends ConfigurationSlice,
    AssetsSlice,
    PreviewSlice,
    AutoSaveSlice,
    DesignHistorySlice,
    RulesMergeHistorySlice {
  // Additional store-wide actions
  resetStore: () => void
}

// Create the combined store
export const useChatBuilderStore = create<ChatBuilderStore>()(
  persist(
    (...args) => {
      const [set, get, _store] = args

      // Create individual slices
      const configSlice = createConfigurationSlice(...args)
      const assetsSlice = createAssetsSlice(...args)
      const previewSlice = createPreviewSlice(...args)
      const autoSaveSlice = createAutoSaveSlice(...args)
      const designHistorySlice = createDesignHistorySlice(...args)
      const rulesMergeHistorySlice = createRulesMergeHistorySlice(...args)

      return {
        // Combine all slices
        ...configSlice,
        ...assetsSlice,
        ...previewSlice,
        ...autoSaveSlice,
        ...designHistorySlice,
        ...rulesMergeHistorySlice,

        // Override auto-save to integrate with configuration state
        triggerAutoSave: async () => {
          const state = get()

          if (
            !state.currentConfiguration ||
            !state.currentConfiguration.id ||
            !state.hasUnsavedChanges ||
            !state.autoSaveEnabled
          ) {
            return
          }

          try {
            // Normal auto-save for real configurations
            await autoSaveSlice.triggerAutoSave()

            // Only clear unsaved changes after successful auto-save
            set({ hasUnsavedChanges: false, lastAutoSave: new Date() })
          } catch (error) {
            // If auto-save fails, keep hasUnsavedChanges as true so it will retry
            console.error(
              'Auto-save failed, will retry on next interval:',
              error
            )
          }
        },

        // Store-wide reset action
        resetStore: () =>
          set({
            // Configuration state
            currentConfiguration: null,
            hasUnsavedChanges: false,
            hasUnpublishedChanges: false,
            pendingUpdateTypes: [],

            // Preview state
            previewData: null,
            productCollections: [],
            brandMaterials: [],

            // Auto-save state
            autoSaveEnabled: false,
            lastAutoSave: null,

            designHistory: EMPTY_DESIGN_HISTORY,
            rulesMergeHistory: EMPTY_RULES_MERGE_HISTORY,

            // Shared state
            isLoading: false,
            error: null,
          }),
      }
    },
    {
      name: 'chat-builder-storage',
      partialize: (state) => ({
        currentConfiguration: state.currentConfiguration,
        autoSaveEnabled: state.autoSaveEnabled,
        hasUnsavedChanges: state.hasUnsavedChanges,
        hasUnpublishedChanges: state.hasUnpublishedChanges,
        lastAutoSave: state.lastAutoSave,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.currentConfiguration) {
          state.currentConfiguration = {
            ...state.currentConfiguration,
            assets: state.currentConfiguration.assets || [],
          }
        }
      },
    }
  )
)

// Auto-save interval hook
let autoSaveInterval: number | null = null
let lastAutoSaveTime = 0

export const useAutoSave = () => {
  const { autoSaveEnabled, triggerAutoSave, hasUnsavedChanges } =
    useChatBuilderStore()

  useEffect(() => {
    if (autoSaveEnabled && hasUnsavedChanges) {
      // Clear existing interval
      if (autoSaveInterval) {
        window.clearTimeout(autoSaveInterval)
        autoSaveInterval = null
      }

      // Only start new interval if enough time has passed since last auto-save
      const now = Date.now()
      const timeSinceLastSave = now - lastAutoSaveTime
      // Use shorter interval for development, longer for production
      const minInterval = process.env.NODE_ENV === 'development' ? 2000 : 30000 // 2s dev, 30s prod

      if (timeSinceLastSave >= minInterval) {
        // Save immediately if enough time has passed
        triggerAutoSave()
        lastAutoSaveTime = now
      } else {
        // Schedule save for remaining time
        const remainingTime = minInterval - timeSinceLastSave
        autoSaveInterval = window.setTimeout(() => {
          triggerAutoSave()
          lastAutoSaveTime = Date.now()
        }, remainingTime)
      }
    } else {
      // Clear any pending auto-save when disabled or no changes
      if (autoSaveInterval) {
        window.clearTimeout(autoSaveInterval)
        autoSaveInterval = null
      }
    }

    return () => {
      if (autoSaveInterval) {
        window.clearTimeout(autoSaveInterval)
        autoSaveInterval = null
      }
    }
  }, [autoSaveEnabled, triggerAutoSave, hasUnsavedChanges])
}

// Re-export types
export * from './designHistorySlice'
export * from './types'
